import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { GROUPS, SITE, SOLUTIONS, solutionBySlug } from "@/config/site";

const navLinkCls = ({ isActive }) =>
  `rounded-sm px-1 py-2 text-sm font-medium transition-colors ${
    isActive ? "text-cobalt" : "text-ink/80 hover:text-cobalt"
  }`;

export default function Navbar() {
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const megaRef = useRef(null);

  useEffect(() => {
    setMobile(false);
    setMegaOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobile]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setMegaOpen(false);
      megaRef.current?.focus();
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-white/95 backdrop-blur-md transition-shadow ${
        scrolled ? "border-silver/60 shadow-sm" : "border-silver/40"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:h-[76px] lg:px-8">
        <Link to="/" className="flex shrink-0 items-center" data-testid="nav-logo-link" aria-label="DJC Global home">
          <img src={SITE.logo} alt="DJC Global" className="h-11 w-auto lg:h-12" width="150" height="52" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          <div
            className="relative"
            onMouseEnter={() => setMegaOpen(true)}
            onMouseLeave={() => setMegaOpen(false)}
          >
            <button
              ref={megaRef}
              type="button"
              aria-expanded={megaOpen}
              aria-haspopup="true"
              data-testid="nav-solutions-megamenu-button"
              onClick={() => setMegaOpen((v) => !v)}
              onKeyDown={onKeyDown}
              className="flex items-center gap-1 rounded-sm px-1 py-2 text-sm font-medium text-ink/80 transition-colors hover:text-cobalt"
            >
              Solutions
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${megaOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {megaOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute left-1/2 top-full w-[880px] -translate-x-1/2 pt-3"
                >
                  <div className="grid grid-cols-4 gap-6 rounded-md border border-silver/60 bg-white p-7 shadow-xl shadow-midnight/5">
                    {GROUPS.map((g) => (
                      <div key={g.slug}>
                        <p className="border-b border-silver/50 pb-2 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-cobalt">
                          {g.name}
                        </p>
                        <ul className="mt-3 space-y-1">
                          {SOLUTIONS.filter((s) => s.group === g.name).map((s) => (
                            <li key={s.id}>
                              <Link
                                to={`/solutions/${s.slug}`}
                                data-testid={`megamenu-item-${s.slug}`}
                                className="group/item block rounded-sm p-2 transition-colors hover:bg-platinum"
                              >
                                <span className="flex items-center gap-1.5 font-display text-[13px] font-semibold leading-snug text-ink group-hover/item:text-cobalt">
                                  {s.name}
                                  <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover/item:opacity-100" />
                                </span>
                                <span className="mt-1 block text-xs leading-relaxed text-ink/60">
                                  {s.menuLabel}
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <div className="col-span-4 flex items-center justify-between border-t border-silver/50 pt-4">
                      <Link
                        to="/solutions"
                        data-testid="megamenu-all-solutions"
                        className="text-sm font-semibold text-cobalt hover:text-midnight"
                      >
                        View all seven destinations
                      </Link>
                      <span className="text-xs text-ink/55">Not sure where to start? Talk with us first.</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <NavLink to="/our-approach" className={navLinkCls} data-testid="nav-approach-link">
            Our Approach
          </NavLink>
          <NavLink to="/about" className={navLinkCls} data-testid="nav-about-link">
            About
          </NavLink>
          <NavLink to="/contact" className={navLinkCls} data-testid="nav-contact-link">
            Contact
          </NavLink>
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-lets-talk-button"
            className="rounded-md bg-cobalt px-5 py-2.5 font-display text-sm font-semibold text-white shadow-sm transition-colors hover:bg-midnight"
          >
            Let's Talk
          </Link>
        </div>

        <button
          type="button"
          className="rounded-sm p-2 text-ink lg:hidden"
          aria-label={mobile ? "Close menu" : "Open menu"}
          aria-expanded={mobile}
          data-testid="nav-mobile-toggle"
          onClick={() => setMobile((v) => !v)}
        >
          {mobile ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 top-16 z-40 overflow-y-auto bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="px-4 py-6 sm:px-6" aria-label="Mobile">
              {GROUPS.map((g) => (
                <div key={g.slug} className="border-b border-silver/50 py-4">
                  <p className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-cobalt">
                    {g.name}
                  </p>
                  <ul className="mt-2 space-y-1">
                    {SOLUTIONS.filter((s) => s.group === g.name).map((s) => (
                      <li key={s.id}>
                        <Link
                          to={`/solutions/${s.slug}`}
                          data-testid={`mobile-menu-item-${s.slug}`}
                          className="block rounded-sm p-2 font-display text-[15px] font-semibold text-ink hover:bg-platinum"
                        >
                          {s.name}
                          <span className="mt-0.5 block font-body text-xs font-normal text-ink/60">
                            {s.menuLabel}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <div className="space-y-1 py-4">
                <Link to="/solutions" data-testid="mobile-all-solutions" className="block rounded-sm p-2 font-display text-[15px] font-semibold text-ink hover:bg-platinum">
                  All Solutions
                </Link>
                <Link to="/our-approach" data-testid="mobile-approach-link" className="block rounded-sm p-2 font-display text-[15px] font-semibold text-ink hover:bg-platinum">
                  Our Approach
                </Link>
                <Link to="/about" data-testid="mobile-about-link" className="block rounded-sm p-2 font-display text-[15px] font-semibold text-ink hover:bg-platinum">
                  About
                </Link>
                <Link to="/contact" data-testid="mobile-contact-link" className="block rounded-sm p-2 font-display text-[15px] font-semibold text-ink hover:bg-platinum">
                  Contact
                </Link>
              </div>
              <Link
                to="/contact"
                data-testid="mobile-lets-talk-button"
                className="mt-2 block rounded-md bg-cobalt px-5 py-4 text-center font-display text-base font-semibold text-white"
              >
                Let's Talk
              </Link>
              <p className="mt-4 pb-6 text-center text-xs text-ink/55">
                Prefer to call? <a href={SITE.phoneHref} data-testid="mobile-phone-link" className="font-semibold text-cobalt">{SITE.phone}</a>
              </p>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
